import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BigNumber, ethers, providers, utils } from 'ethers';
import {
  WalletProvider,
  WalletProviderSymbol,
} from '../../datas/walletProviders';
import { RootState } from '../store';
import { ChainExtended, ChainToken } from './bridgeSlice';

// TODO : 토큰에 따른 올바른 ABI 가져오기
import emdmAbiJson from '../../abis/emdm.abi.json';
import KSTADIUM from '../../services/kstadium.service';
import UnsupportedChainIdError from '../../errors/UnsupportedChainIdError';
import KStadiumNeedLoginError from '../../errors/KStadiumNeedLoginError';
import { validateJWT } from '../../utils/jwt';
import HEXLANT from '../../utils/hexlant';

export const STORAGE_FROM_CHAIN_WALLET_PROVIDER_KEY = `fromChainWalletProviderSymbol`;
export const SESSION_TIMEOUT_SECONDS = 60 * 60 * 1000;

type WalletState = {
  fromChainWalletProvider: WalletProvider | null;
  toChainWalletProvider: WalletProvider | null;
  connectedWallets: Partial<Record<WalletProviderSymbol, string>>;

  // UTC기준으로 시간 저장
  sessionExpiredAt: number;
};

const initialState: WalletState = {
  fromChainWalletProvider: null,
  toChainWalletProvider: null,
  connectedWallets: {},

  sessionExpiredAt: new Date().getTime() + SESSION_TIMEOUT_SECONDS,
};

interface ConnectWalletActionResult {
  walletProvider: WalletProvider;
  walletAddress: string | null | undefined;
}

export const connectWallet = async ({
  chain,
  provider,
}: {
  chain: ChainExtended;
  provider: WalletProvider;
}): Promise<ConnectWalletActionResult> => {
  if (provider.symbol === `METAMASK`) {
    if (typeof window.ethereum !== `undefined` && chain) {
      const [account]: string[] = await window.ethereum.request({
        method: `eth_requestAccounts`,
      });
      // chain ID 받아오기
      const chainIdHex = await window.ethereum.request({
        method: `eth_chainId`,
      });
      const selectedChainId = Number(BigNumber.from(chainIdHex.toString()));
      const selectedEvmChainId = Number(
        BigNumber.from(chain.evmChainId.toString()),
      );
      // 지갑 체인이 선택된 from 체인과 다르면 Error 띄우기
      if (selectedChainId !== selectedEvmChainId) {
        throw new UnsupportedChainIdError(Number(chainIdHex));
      }

      return {
        walletProvider: provider,
        walletAddress: account,
      };
    }
  }

  if (provider.symbol === `KSTADIUM`) {
    // JWT 존재여부, 유효성 체크 (토큰 갱신으로 유효성 체크)
    const accessToken = KSTADIUM.getAccessToken();
    if (accessToken) {
      try {
        validateJWT(accessToken, true);
        const payload = KSTADIUM.getTokenPayload();
        // 유효하면 지갑 주소 파싱후 저장
        return {
          walletProvider: provider,
          walletAddress: payload?.info.account || null,
        };
      } catch (e) {
        console.error(e);
      }
    }
    // IF: 유효하지 않으면 로그인 모달 띄우기
    throw new KStadiumNeedLoginError();
  }

  throw new Error(`Unknown Wallet Provider`);
};

export const getWalletBalance = async (
  token: ChainToken,
  provider: WalletProvider,
  address: string,
): Promise<BigNumber | undefined> => {
  let _balance: BigNumber | undefined;
  if (provider.symbol === `METAMASK`) {
    try {
      if (typeof window.ethereum === `undefined`) {
        return undefined;
      }
      if (token.address != null) {
        const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
        const contract = new ethers.Contract(
          token.address,
          emdmAbiJson,
          web3Provider,
        );
        _balance = await contract.balanceOf(address);
      } else {
        const web3Provider = new providers.Web3Provider(window.ethereum);
        _balance = await web3Provider.getBalance(address);
      }
    } catch (e) {
      console.error(e);
      console.error(`METAMASK Wallet Balance Error`);
      return undefined;
    }
  }
  if (provider.symbol === `KSTADIUM`) {
    try {
      if (process.env.REACT_APP_ENV === `development`) {
        const { data } = await HEXLANT.hexlantBalanceDev();
        // NOTE : KSTA_DEV로 DB에 표기되어 있어서 includes로 비교 가능함
        const tokenBalance = data.coins.find((coin) =>
          coin.tokenSymbol.includes(token.symbol),
        );

        _balance =
          tokenBalance &&
          BigNumber.from(
            utils.parseUnits(tokenBalance.balance, token.decimals).toString(),
          );
      } else {
        const { data } = await HEXLANT.hexlantBalance();
        // NOTE : inKSTA가 있어 includes로 비교 불가능
        const tokenBalance = data.coins.find(
          (coin) => coin.tokenSymbol === token.symbol,
        );

        _balance =
          tokenBalance &&
          BigNumber.from(
            utils.parseUnits(tokenBalance.balance, token.decimals).toString(),
          );
      }
    } catch (e) {
      console.error(e);
      console.error(`K STADIUM Wallet Balance Error`);
      return undefined;
    }
  }
  return _balance;
};

const walletSlice = createSlice({
  name: `wallet`,
  initialState,
  reducers: {
    resetSessionExpiredAt: (state) => {
      state.sessionExpiredAt = new Date().getTime() + SESSION_TIMEOUT_SECONDS;
    },

    resetWallet: () => {
      sessionStorage.removeItem(STORAGE_FROM_CHAIN_WALLET_PROVIDER_KEY);

      return initialState;
    },
    setFromChainWalletProvider: (
      state,
      action: PayloadAction<WalletState['fromChainWalletProvider']>,
    ) => {
      if (action.payload) {
        sessionStorage.setItem(
          STORAGE_FROM_CHAIN_WALLET_PROVIDER_KEY,
          action.payload.symbol,
        );
      }

      state.fromChainWalletProvider = action.payload;
    },
    setToChainWalletProvider: (
      state,
      action: PayloadAction<WalletState['toChainWalletProvider']>,
    ) => {
      state.toChainWalletProvider = action.payload;
    },
    setConnectedWallet: (
      state,
      action: PayloadAction<ConnectWalletActionResult>,
    ) => {
      const { walletProvider, walletAddress } = action.payload;
      state.connectedWallets = {
        ...state.connectedWallets,
        [walletProvider.symbol]: walletAddress,
      };
    },

    setDisconnectWallet: (
      state,
      action: PayloadAction<WalletProviderSymbol>,
    ) => {
      const newConnectWallets = { ...state.connectedWallets };
      delete newConnectWallets[action.payload];
      state.connectedWallets = newConnectWallets;
    },
  },
  extraReducers: (builder) => {},
});

export const {
  resetSessionExpiredAt,
  resetWallet,
  setFromChainWalletProvider,
  setToChainWalletProvider,
  setConnectedWallet,
  setDisconnectWallet,
} = walletSlice.actions;

export default walletSlice.reducer;

export const selectWalletSlice = (state: RootState): WalletState =>
  state.wallet;
